import { useEffect, useRef, useState } from "react"
import type { KeyboardEvent } from "react"
import type { NutritionQueue, NutritionQueueReceipt } from "@nova-ai/shared"
import { Check, Circle, CircleAlert, ChevronRight, Clock, Search, X } from "lucide-react"
import { cn } from "../../lib/utils"
import type { NutritionQueueSearch } from "../../services/use-nutrition-queue"
import { Button } from "../ui/button"
import { StatusBadge } from "../ui/status-badge"
import {
  NUTRITION_STEPS,
  NUTRITION_VIEWS,
  nutritionDuration,
  nutritionOutcome,
} from "./nutrition-presentation"

type Props = {
  data: NutritionQueue
  rows: NutritionQueueReceipt[]
  search: NutritionQueueSearch
  onChange: (search: NutritionQueueSearch) => void
}
export function NutritionEvents({ data, rows, search, onChange }: Props) {
  return (
    <section id="nutrition-events" aria-label="Nutrition receipts" className="min-w-0 space-y-4">
      <NutritionViewTabs data={data} search={search} onChange={onChange} />

      <div className="min-w-0 rounded-2xl border border-border bg-card shadow-card max-sm:rounded-lg overflow-hidden">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-4 py-3 max-sm:px-3">
          <NutritionSearch
            value={search.q ?? ""}
            onChange={(q) => onChange({ ...search, q: q || undefined })}
          />

          {search.step && (
            <Button className="min-h-11" onClick={() => onChange({ ...search, step: undefined })}>
              {`At step: ${NUTRITION_STEPS.find((step) => step.key === search.step)?.short}`}

              <X className="size-4" aria-hidden="true" />
            </Button>
          )}

          <span className="text-xs text-muted-foreground sm:ml-auto">
            {data.counts[NUTRITION_VIEWS.find((view) => view.key === search.view)?.count ?? "all"]}{" "}
            receipts
          </span>
        </div>

        <div className="grid min-h-9 grid-cols-[minmax(0,2fr)_104px_minmax(0,1.6fr)_88px_112px_16px] items-center gap-4 border-b border-border px-5 text-[12px] text-muted-foreground max-xl:hidden">
          <span>Product</span>

          <span>Received</span>

          <span>Progress</span>

          <span>Image time</span>

          <span>Outcome</span>

          <span />
        </div>

        {rows.map((row) => (
          <NutritionEventRow
            key={row.receipt.id}
            row={row}
            observedAt={data.observedAt}
            onOpen={() =>
              onChange({
                ...search,
                event: row.receipt.id,
                store: undefined,
                sku: undefined,
                scope: undefined,
                scopes: undefined,
              })
            }
          />
        ))}

        {rows.length === 0 && (
          <div className="space-y-3 px-5 py-10 text-center">
            <p className="text-sm">
              {search.view === "needs_attention" && !search.q && !search.step
                ? "Nothing needs attention in this period."
                : "No receipts match these filters."}
            </p>

            <Button
              className="min-h-11"
              onClick={() =>
                onChange({ view: "all", ...(search.period ? { period: search.period } : {}) })
              }
            >
              Clear filters
            </Button>
          </div>
        )}
      </div>
    </section>
  )
}

function NutritionSearch({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) {
  const [draft, setDraft] = useState({ source: value, text: value })
  if (draft.source !== value) {
    setDraft({ source: value, text: value })
  }
  const currentDraft = draft.source === value ? draft.text : value
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])
  useEffect(() => {
    if (currentDraft === value) {
      return
    }
    const timeout = setTimeout(() => onChangeRef.current(currentDraft), 250)
    return () => clearTimeout(timeout)
  }, [currentDraft, value])
  return (
    <label className="relative block w-full sm:max-w-[360px]">
      <Search
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground"
        aria-hidden="true"
      />

      <span className="sr-only">Search nutrition receipts</span>

      <input
        type="search"
        value={currentDraft}
        maxLength={128}
        onChange={(event) => setDraft({ source: value, text: event.target.value })}
        placeholder="Product, SKU or message ID"
        className="h-11 w-full rounded-full border border-border bg-card pl-[34px] pr-3 text-base placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:h-8 sm:text-[13px]"
      />
    </label>
  )
}

function NutritionViewTabs({ data, search, onChange }: Omit<Props, "rows">) {
  const refs = useRef<(HTMLButtonElement | null)[]>([])
  const onKeyDown = (event: KeyboardEvent, index: number) => {
    const offsets: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1 }
    const offset = offsets[event.key]
    let next: number | null = null
    if (event.key === "Home") {
      next = 0
    } else if (event.key === "End") {
      next = NUTRITION_VIEWS.length - 1
    } else if (offset !== undefined) {
      next = (index + offset + NUTRITION_VIEWS.length) % NUTRITION_VIEWS.length
    }
    if (next === null) {
      return
    }
    event.preventDefault()
    const view = NUTRITION_VIEWS[next]
    if (view) {
      refs.current[next]?.focus()
      onChange({ ...search, view: view.key })
    }
  }
  return (
    <div
      role="tablist"
      aria-label="Receipt views"
      className="mt-4 flex gap-5 overflow-x-auto border-b border-border [scrollbar-width:none] max-sm:-mx-4 max-sm:mt-2 max-sm:px-4"
    >
      {NUTRITION_VIEWS.map(({ key, label, count }, index) => (
        <button
          type="button"
          role="tab"
          className="-mb-px inline-flex min-h-11 items-center gap-1.5 border-b-2 border-transparent px-0.5 text-[14px] whitespace-nowrap text-muted-foreground focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring aria-selected:border-foreground aria-selected:font-medium aria-selected:text-foreground max-sm:min-w-11 max-sm:shrink-0"
          key={key}
          aria-selected={search.view === key}
          tabIndex={search.view === key ? 0 : -1}
          ref={(element) => {
            refs.current[index] = element
          }}
          onKeyDown={(event) => onKeyDown(event, index)}
          onClick={() => onChange({ ...search, view: key })}
        >
          {label}

          <span
            className={cn(
              "text-xs tabular-nums",
              key === "needs_attention" && data.counts[count] > 0
                ? "rounded-full bg-warning/10 px-1.5 font-semibold text-warning"
                : "text-muted-foreground"
            )}
          >
            {data.counts[count]}
          </span>
        </button>
      ))}
    </div>
  )
}

function NutritionEventRow({
  row,
  observedAt,
  onOpen,
}: {
  row: NutritionQueueReceipt
  observedAt: string
  onOpen: () => void
}) {
  const { receipt } = row
  const outcome = nutritionOutcome(row.outcome)
  const imageElapsed = receiptImageElapsed(row, observedAt)
  return (
    <button
      type="button"
      onClick={onOpen}
      className="grid w-full grid-cols-[minmax(0,2fr)_104px_minmax(0,1.6fr)_88px_112px_16px] items-center gap-4 border-b border-border px-5 py-3.5 text-left last:border-b-0 hover:bg-[color-mix(in_oklch,var(--color-muted)_45%,var(--color-card))] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring max-xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_16px] max-xl:[grid-template-areas:'prod_track_status_chev'_'when_track_took_chev'] max-xl:gap-x-5 max-xl:gap-y-0.5 max-sm:grid-cols-[minmax(0,1fr)_auto] max-sm:[grid-template-areas:'prod_status'_'track_track'_'when_took'] max-sm:gap-x-3 max-sm:gap-y-2.5 max-sm:px-3"
    >
      <span className="min-w-0 max-xl:[grid-area:prod]">
        <span className="block truncate text-sm font-medium">
          {row.productName ??
            (receipt.productId ? `Product ${receipt.productId}` : `Receipt #${receipt.id}`)}
        </span>

        <span className="block truncate text-xs text-muted-foreground">{`${row.skus[0] ?? "SKU unresolved"}${row.skus.length > 1 ? ` + ${row.skus.length - 1} more SKUs` : ""} · ${row.stores.length} stores`}</span>
      </span>

      <span
        className="min-w-0 max-xl:flex max-xl:gap-1.5 max-xl:[grid-area:when] text-[13px] tabular-nums"
        title={`Receipt #${receipt.id}`}
      >
        <time dateTime={receipt.receivedAt}>
          {new Date(receipt.receivedAt).toLocaleDateString("sk-SK")}
        </time>

        <span className="block text-xs text-muted-foreground">
          {new Date(receipt.receivedAt).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      </span>

      <span className="min-w-0 max-xl:[grid-area:track]">
        <NutritionProgress row={row} />

        <span className="mt-1.5 block text-xs text-muted-foreground">{`${receipt.summary.upload.complete} of ${receipt.summary.scopes} scopes uploaded · ${receipt.summary.publication.complete} published`}</span>
      </span>

      <span
        className={cn(
          "min-w-0 max-xl:text-right max-xl:[grid-area:took] text-[13px] tabular-nums",
          imageElapsed !== null && imageElapsed > 600000 ? "text-warning" : "text-muted-foreground"
        )}
      >
        <span
          className="inline-flex items-center gap-1"
          title={
            imageElapsed !== null && imageElapsed > 600000
              ? "Over the 10-minute image target"
              : undefined
          }
        >
          {imageElapsed !== null && imageElapsed > 600000 && <Clock className="size-3" />}

          {nutritionDuration(receipt.validation === "rejected" ? null : imageElapsed)}
        </span>

        <span className="sr-only">{row.imageCompleteAt ? "image time" : "since receipt"}</span>

        {row.publicationWaitingMs !== null && (
          <span className="mt-1 block">{`Publication waiting ${nutritionDuration(row.publicationWaitingMs)}`}</span>
        )}
      </span>

      <span className="min-w-0 max-xl:text-right max-xl:[grid-area:status]">
        <StatusBadge
          size="sm"
          tone={outcome.tone}
          className="max-w-full whitespace-normal text-left"
        >
          <span className="size-1.5 shrink-0 rounded-full bg-current" />

          {outcome.label}
        </StatusBadge>
      </span>

      <ChevronRight
        className="max-xl:[grid-area:chev] max-sm:hidden size-4 text-muted-foreground"
        aria-hidden="true"
      />
    </button>
  )
}

function NutritionProgress({ row }: { row: NutritionQueueReceipt }) {
  const summary = row.receipt.summary
  const states = [
    row.receipt.validation === "accepted" ? "complete" : "failed",
    summary.generation,
    summary.upload,
    summary.publication,
  ].map(progressState)
  return (
    <span
      className="flex max-w-[200px] items-center max-sm:max-w-none"
      role="img"
      aria-label={NUTRITION_STEPS.map((step, index) => `${step.short}: ${states[index]}`).join(
        ", "
      )}
    >
      {states.map((state, index) => (
        <span key={index} className="contents">
          <span
            className={cn(
              "inline-flex size-5 flex-none items-center justify-center rounded-full border-[1.5px]",
              PROGRESS_STYLES[state]
            )}
          >
            <ProgressIcon state={state} />
          </span>

          {index < 3 && (
            <span
              className={cn(
                "h-0.5 min-w-2.5 flex-1",
                state === "complete" ? "bg-success" : "bg-border"
              )}
            />
          )}
        </span>
      ))}
    </span>
  )
}

const PROGRESS_STYLES = {
  complete: "border-success bg-success text-success-foreground",
  failed: "border-destructive bg-destructive text-destructive-foreground",
  pending: "border-info text-info",
  unknown: "border-border text-muted-foreground",
} as const
function progressState(
  stage: string | NutritionQueueReceipt["receipt"]["summary"]["generation"]
): keyof typeof PROGRESS_STYLES {
  if (typeof stage === "string") {
    return stage === "complete" ? "complete" : "failed"
  }
  if (stage.failed > 0) {
    return "failed"
  }
  if (stage.total > 0 && stage.complete === stage.total) {
    return "complete"
  }
  if (stage.pending > 0) {
    return "pending"
  }
  return "unknown"
}
function ProgressIcon({ state }: { state: keyof typeof PROGRESS_STYLES }) {
  if (state === "complete") {
    return <Check className="size-3.5" />
  }
  if (state === "failed") {
    return <CircleAlert className="size-3.5" />
  }
  return <Circle className="size-2" />
}

function receiptImageElapsed(row: NutritionQueueReceipt, observedAt: string) {
  if (row.imageElapsedMs !== null) {
    return row.imageElapsedMs
  }
  if (!row.imageCompleteAt && row.group !== "done") {
    return Math.max(0, Date.parse(observedAt) - Date.parse(row.receipt.receivedAt))
  }
  return null
}
